import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText, Printer, BarChart3, Clock, CheckCircle, FileDown,
  Search, Upload, Eye, X, ChevronLeft, ChevronRight, Download,
} from 'lucide-react';
import { useGetClassesQuery } from '../../services/polesOptionsApi';
import { useGetAnneesAcademiquesQuery, useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import {
  useLazyGetDonneesReleveQuery,
  useLazyGetDonneesClasseQuery,
  useEnregistrerImpressionMutation,
  useGetHistoriqueImpressionsQuery,
  useLazyGetQrDefinitifExistantQuery,
  type DonneesReleve,
  type ReleveLegerAgent,
} from '../../services/relevesApi';
import { RelevePreview } from '../../components/Releves/RelevePreview';
import { useGetInfoInstitutQuery } from '../../services/coreApi';
import { genererPdfMatieresARattraper } from './matieresARattraperPdf';
import { store } from '../../store';
import { documentA4, imprimerFenetreA4 } from '../../utils/impressionA4';

// ─── Types locaux ─────────────────────────────────────────────────────────────

type ActiveTab = 'impression' | 'historique';
type TypeReleve = 'provisoire' | 'definitif';
type ScopeReleve = 'individuel' | 'classe' | 'filtre';

// ─── Helper : génère le HTML imprimable pour un étudiant ─────────────────────

function buildReleveHTML(data: DonneesReleve, type: TypeReleve, qr?: string | null, ref?: string): string {
  const isProvisoire = type === 'provisoire';
  const fN = (v: number | null | undefined) => (v == null ? '—' : v.toFixed(2).replace('.', ','));
  const getMention = (m: number | null) => {
    if (m == null) return '—';
    if (m >= 16) return 'Très Bien';
    if (m >= 14) return 'Bien';
    if (m >= 12) return 'Assez Bien';
    if (m >= 10) return 'Passable';
    return 'Ajourné';
  };
  const ordinal = (n: number) => n === 1 ? '1er' : `${n}e`;
  const semCum = (niv: string, sem: number) => {
    const off: Record<string, number> = { L1: 0, L2: 2, L3: 4, M1: 0, M2: 2 };
    return (off[niv] ?? 0) + sem;
  };

  const e = data.etudiant;
  const rs = data.resultat_semestre;
  const dateStr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  const mention = getMention(rs?.moyenne_semestre ?? null);
  const sc = semCum(e.niveau, data.semestre);
  const b = '1px solid #000';

  // UE + ECUE rows
  const uesHTML = data.ues.map(ue => {
    // UE header row (fond clair, cellules individuelles)
    const ueRow = `<tr style="background:#f0f0f0">
      <td style="border:${b};padding:3px 4px;font-weight:700;font-size:8.5px">${ue.code_ue} — ${ue.nom_ue}</td>
      <td style="border:${b}"></td><td style="border:${b}"></td><td style="border:${b}"></td><td style="border:${b}"></td><td style="border:${b}"></td>
      <td style="border:${b};text-align:center;font-weight:700;font-size:8.5px;padding:3px 2px">${ue.credits_total}</td>
      <td style="border:${b};text-align:center;font-weight:700;font-size:8.5px;padding:3px 2px">${fN(ue.moyenne_ue)}</td>
      <td style="border:${b};text-align:center;font-weight:700;font-size:8px;padding:3px 2px;color:${ue.validee ? '#006600' : '#CC0000'}">${ue.validee ? 'Validé' : 'Non validé'}</td>
    </tr>`;

    const matRows = ue.matieres.map(mat => {
      const mD = mat.moy_devoirs != null ? mat.moy_devoirs * 0.4 : null;
      const mE = mat.note_examen != null ? mat.note_examen * 0.6 : null;
      const d1 = mat.absent_d1 ? '<span style="color:#C00">AB</span>' : fN(mat.note_d1);
      const d2 = mat.absent_d2 ? '<span style="color:#C00">AB</span>' : fN(mat.note_d2);
      const md = (mat.absent_d1 && mat.absent_d2) ? '<span style="color:#C00">AB</span>' : fN(mD);
      const ex = mat.absent_examen
        ? '<span style="color:#C00">AB</span>'
        : fN(mat.note_examen) + (mat.est_rattrapage ? '<span style="color:#D97706;font-size:7px"> (R)</span>' : '');
      const me = mat.absent_examen ? '<span style="color:#C00">AB</span>' : fN(mE);
      return `<tr>
        <td style="border:${b};padding:2px 4px 2px 14px;font-size:8.5px">${mat.nom_matiere}</td>
        <td style="border:${b};text-align:center;font-size:8.5px;padding:2px">${d1}</td>
        <td style="border:${b};text-align:center;font-size:8.5px;padding:2px">${d2}</td>
        <td style="border:${b};text-align:center;font-size:8.5px;padding:2px">${md}</td>
        <td style="border:${b};text-align:center;font-size:8.5px;padding:2px">${ex}</td>
        <td style="border:${b};text-align:center;font-size:8.5px;padding:2px">${me}</td>
        <td style="border:${b};text-align:center;font-size:8.5px;padding:2px">${mat.credits}</td>
        <td style="border:${b};text-align:center;font-size:8.5px;padding:2px;font-weight:700">${fN(mat.moyenne_ecue)}</td>
        <td style="border:${b}"></td>
      </tr>`;
    }).join('');

    return ueRow + matRows;
  }).join('');

  // Photo + QR
  const photoHTML = e.photo_url
    ? `<img src="${e.photo_url}" style="width:78px;height:78px;object-fit:cover;border:1px solid #999"/>`
    : `<div style="width:78px;height:78px;border:1px dashed #bbb;display:flex;align-items:center;justify-content:center;font-size:8px;color:#aaa">Photo</div>`;

  let qrHTML = '';
  if (type === 'definitif') {
    qrHTML = qr
      ? `<img src="${qr}" style="width:78px;height:78px;border:1px solid #999"/>`
      : `<div style="width:78px;height:78px;border:1px dashed #bbb;display:flex;align-items:center;justify-content:center;font-size:8px;color:#aaa;text-align:center">QR Code</div>`;
  }

  const refHTML = (type === 'definitif' && ref)
    ? `<div style="font-size:7px;font-family:monospace;margin-top:2px;text-align:center;word-break:break-all">${ref}</div>`
    : '';

  const dateNceStr = e.date_naissance
    ? `${e.date_naissance}${e.lieu_naissance ? ' à ' + e.lieu_naissance : ''}`
    : (e.lieu_naissance || '');

  const watermark = isProvisoire
    ? `<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-45deg);font-size:110px;font-weight:900;opacity:0.06;color:#888;z-index:0;pointer-events:none;user-select:none;white-space:nowrap;letter-spacing:8px">PROVISOIRE</div>`
    : '';

  return `<!DOCTYPE html>
<html lang="fr"><head><meta charset="UTF-8"/><title>Relevé ${e.matricule}</title>
<style>
  html,body{margin:0;padding:0}
  body{font-family:Arial,Helvetica,sans-serif;padding:0.8cm 1.2cm;color:#000;font-size:11px}
  @page{size:A4 portrait;margin:0}
  table{border-collapse:collapse}
</style>
</head><body>
${watermark}
<div style="position:relative;z-index:1">

<!-- EN-TÊTE -->
<table style="width:100%;margin-bottom:4px"><tr>
  <td style="width:55%;vertical-align:top;padding-right:6px">
    <div style="font-weight:700;font-size:9px;text-transform:uppercase;line-height:1.3">
      MINISTÈRE DE L'ENSEIGNEMENT SUPERIEUR DE LA RECHERCHE SCIENTIFIQUE ET DE L'INNOVATION TECHNOLOGIQUE
    </div>
    <div style="background:#FFD700;font-weight:700;font-size:11px;padding:2px 5px;margin:3px 0;display:inline-block">
      INSTITUT INTERNATIONAL POLYTECHNIQUE &amp; COMMERCE 2I
    </div>
    <div style="font-size:9px;line-height:1.5">
      <div>Tél. : <b>+242 06 736 10 00</b></div>
      <div>E-mail : direction2i@institut-international-2i.com</div>
      <div>Adresse : 06, RUE LI-LELEMB BASE INDUSTRIELLE</div>
      <div style="padding-left:55px">Ville : POINTE-NOIRE</div>
    </div>
  </td>
  <td style="width:25%;text-align:center;vertical-align:middle">
    <div style="background:#FFD700;font-weight:700;font-size:12px;padding:3px 6px;display:inline-block">RELEVÉ DE NOTES</div>
    <div style="background:#1F4788;color:#fff;font-weight:700;font-size:11px;padding:2px 10px;margin-top:5px;display:inline-block;border-radius:3px">Semestre ${sc}</div>
  </td>
  <td style="width:20%;text-align:center;vertical-align:top">
    <div style="font-weight:700;font-size:9px">REPUBLIQUE DU<br/>CONGO</div>
    <div style="font-size:8px;margin-bottom:3px">Unité-Travail-Progrès</div>
    <img src="/logo.png" style="width:55px;height:55px;object-fit:contain" alt="Logo"/>
  </td>
</tr></table>

<div style="border-top:3px solid #000;margin-bottom:5px"></div>

<!-- ANNÉE / CYCLE -->
<table style="width:100%;border:${b};margin-bottom:6px"><tr>
  <td style="padding:3px 8px;border-right:${b};width:50%;font-size:10px">Année Académique : <b>${data.annee_academique}</b></td>
  <td style="padding:3px 8px;width:50%;font-size:10px">Cycle : <b>${e.cycle}</b></td>
</tr></table>

<!-- INFOS ÉTUDIANT -->
<table style="width:100%;margin-bottom:6px"><tr>
  <td style="vertical-align:top">
    <table style="font-size:10px;line-height:1.7">
      <tr><td style="padding-right:8px">Matricule :</td><td style="font-weight:700">${e.matricule}</td></tr>
      <tr><td style="padding-right:8px">Noms :</td><td style="font-weight:700">${e.nom.toUpperCase()}</td></tr>
      <tr><td style="padding-right:8px">Prénoms :</td><td style="font-weight:700">${e.prenoms}</td></tr>
      ${dateNceStr ? `<tr><td style="padding-right:8px;white-space:nowrap">Date Nce :</td><td style="font-weight:700">${dateNceStr}</td></tr>` : ''}
      <tr><td style="padding-right:8px">Niveau :</td><td style="font-weight:700">${e.classe}</td></tr>
      <tr><td style="padding-right:8px">Option :</td><td style="font-weight:700">${e.option}</td></tr>
    </table>
  </td>
  <td style="width:180px;vertical-align:top;text-align:right">
    <div style="display:flex;gap:6px;justify-content:flex-end;align-items:flex-start">
      ${photoHTML}
      ${qrHTML}
    </div>
    ${refHTML}
  </td>
</tr></table>

<!-- TABLEAU DES NOTES -->
<table style="width:100%;font-size:9px"><thead>
  <tr style="background:#FFD700">
    <th style="border:${b};padding:4px 4px;text-align:left;font-weight:700;font-size:8px;width:28%">Modules/U.E</th>
    <th style="border:${b};padding:4px 2px;text-align:center;font-weight:700;font-size:8px;width:6%">D.1</th>
    <th style="border:${b};padding:4px 2px;text-align:center;font-weight:700;font-size:8px;width:6%">D.2</th>
    <th style="border:${b};padding:4px 2px;text-align:center;font-weight:700;font-size:8px;width:8%">Moy.D<br/>(40%)</th>
    <th style="border:${b};padding:4px 2px;text-align:center;font-weight:700;font-size:8px;width:7%">Examen</th>
    <th style="border:${b};padding:4px 2px;text-align:center;font-weight:700;font-size:8px;width:8%">Moy.Ex<br/>(60%)</th>
    <th style="border:${b};padding:4px 2px;text-align:center;font-weight:700;font-size:8px;width:7%">Crédits</th>
    <th style="border:${b};padding:4px 2px;text-align:center;font-weight:700;font-size:8px;width:8%">Moy.EC<br/>UE</th>
    <th style="border:${b};padding:4px 2px;text-align:center;font-weight:700;font-size:8px;width:8%">Décision</th>
  </tr>
</thead><tbody>
${uesHTML}
<tr style="background:#FFD700">
  <td colspan="6" style="border:${b};padding:4px 4px;font-weight:700;font-size:9px">MOYENNE GENERALE</td>
  <td style="border:${b};text-align:center;font-weight:700;font-size:9px;padding:4px 2px">${rs ? fN(rs.credits_obtenus) : '—'}</td>
  <td style="border:${b};text-align:center;font-weight:700;font-size:10px;padding:4px 2px">${fN(rs?.moyenne_semestre ?? null)}</td>
  <td style="border:${b};text-align:center;font-weight:700;font-size:8.5px;padding:4px 2px">${rs == null ? '—' : rs.valide ? 'Admis' : 'Ajourné'}</td>
</tr>
</tbody></table>

<!-- RÉSUMÉ SEMESTRIEL -->
<table style="width:100%;margin-top:8px;font-size:10px">
<tr>
  <td style="border:${b};padding:4px 8px;width:30%;background:#FFD700;font-weight:700">MOYENNE GENERALE</td>
  <td style="border:${b};padding:4px 8px;width:20%;background:#FFD700;font-weight:700;font-size:13px">${fN(rs?.moyenne_semestre ?? null)}</td>
  <td style="border:${b};padding:4px 8px;width:15%;background:#FFD700;font-weight:700">RANG</td>
  <td style="border:${b};padding:4px 8px;width:35%;background:#FFD700;font-weight:700;font-size:13px">${rs?.rang != null ? ordinal(rs.rang) : '—'}</td>
</tr>
<tr>
  <td style="border:${b};padding:4px 8px;background:#FFD700;font-weight:700">NOMBRE DE CREDIT</td>
  <td colspan="3" style="border:${b};padding:4px 8px;background:#FFD700;font-weight:700;font-size:13px">${rs ? fN(rs.credits_obtenus) : '—'}</td>
</tr>
<tr>
  <td style="border:${b};padding:4px 8px;background:#FFD700;font-weight:700">MENTION</td>
  <td colspan="3" style="border:${b};padding:4px 8px;background:#FFD700;font-weight:700;font-size:12px">${mention}</td>
</tr>
<tr>
  <td style="border:${b};padding:4px 8px;background:#FFD700;font-weight:700">DECISION</td>
  <td style="border:${b};padding:4px 8px;font-weight:700;font-size:12px;background:${rs == null ? '#999' : rs.valide ? '#00AA00' : '#CC0000'};color:#fff">${rs == null ? '—' : rs.valide ? 'SEMESTRE VALIDE' : 'AJOURNÉ'}</td>
  <td colspan="2" style="border:${b};padding:4px 8px;font-size:9px">
    Fait à Pointe-Noire, le ${dateStr}<br/><br/>
    Le Chef de Service de La Scolarité et Examens<br/><br/>
    <span style="font-size:8px;color:#666">(Signature et Cachet)</span>
  </td>
</tr>
</table>

</div>
</body></html>`;
}

// ─── Composant principal ──────────────────────────────────────────────────────

const RelevesNotes: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Onglets ──
  const [activeTab, setActiveTab] = useState<ActiveTab>('impression');

  // ── Paramètres d'impression ──
  const [typeReleve, setTypeReleve] = useState<TypeReleve>('provisoire');
  const [scope, setScope] = useState<ScopeReleve>('individuel');
  // Année académique : initialisée sur l'année active dès qu'elle est chargée
  // (l'utilisateur peut ensuite en choisir une autre pour consulter l'archive).
  // On garde une valeur "vide" tant que l'année active n'est pas connue plutôt
  // que de figer 2024-2025 — sinon le sélecteur reste bloqué sur une année
  // obsolète même après changement d'année académique.
  const { data: anneeActive } = useGetAnneeActiveQuery();
  // Année de référence du formulaire : l'année de BASCULE choisie dans le
  // header (localStorage 'annee_academique') si présente, sinon l'année
  // active. Permet d'imprimer directement les relevés de l'année consultée
  // sans la re-sélectionner à la main (2026-10-14).
  const anneeBascule = typeof window !== 'undefined' ? localStorage.getItem('annee_academique') : null;
  const [anneeAcademique, setAnneeAcademique] = useState<string>(anneeBascule || '');
  const [anneeUserModified, setAnneeUserModified] = useState(false);
  useEffect(() => {
    if (anneeUserModified) return;
    const defaut = anneeBascule || anneeActive?.libelle;
    if (defaut && anneeAcademique !== defaut) {
      setAnneeAcademique(defaut);
    }
  }, [anneeActive?.libelle, anneeBascule, anneeUserModified, anneeAcademique]);
  const [semestre, setSemestre] = useState<1 | 2>(1);

  // ── Individuel ──
  const [matricule, setMatricule] = useState('');
  const [matriculeRecherche, setMatriculeRecherche] = useState('');

  // ── Lire les paramètres URL (depuis la fiche étudiant) ──
  useEffect(() => {
    const urlMatricule = searchParams.get('matricule');
    const urlType = searchParams.get('type');
    if (urlMatricule) {
      setMatricule(urlMatricule);
      setMatriculeRecherche(urlMatricule);
      setScope('individuel');
      setActiveTab('impression');
    }
    if (urlType === 'provisoire' || urlType === 'definitif') {
      setTypeReleve(urlType);
    }
    // Nettoyer les params URL après lecture
    if (urlMatricule || urlType) {
      setSearchParams({}, { replace: true });
    }
  }, []);

  // ── Classe ──
  const [classeCode, setClasseCode] = useState('');
  const [poleFilter, setPoleFilter] = useState('');

  // ── Filtre par moyenne ──
  const [moyMin, setMoyMin] = useState('');
  const [moyMax, setMoyMax] = useState('');

  // ── QR code ──
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [numeroReference, setNumeroReference] = useState('');
  const qrInputRef = useRef<HTMLInputElement>(null);

  // ── Prévisualisation ──
  const [previewList, setPreviewList] = useState<DonneesReleve[]>([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  // ── Filtres historique ──
  const [histAnnee, setHistAnnee] = useState('');
  const [histType, setHistType] = useState('');
  const [histDateDebut, setHistDateDebut] = useState('');
  const [histDateFin, setHistDateFin] = useState('');

  // ── Chargement ──
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');

  // ── API ──
  const { data: classes = [] } = useGetClassesQuery({ actif: true });
  const { data: annees = [] } = useGetAnneesAcademiquesQuery({});
  const [fetchDonneesReleve] = useLazyGetDonneesReleveQuery();
  const [fetchDonneesClasse] = useLazyGetDonneesClasseQuery();
  const [enregistrerImpression] = useEnregistrerImpressionMutation();
  const [fetchQrExistant] = useLazyGetQrDefinitifExistantQuery();
  // Info affichée quand le QR + référence ont été repris d'une impression précédente.
  const [qrRepris, setQrRepris] = useState<string | null>(null);

  // Réimpression d'un relevé DÉFINITIF : si l'étudiant a déjà un QR + n° de
  // référence émis, les reprendre automatiquement pour que rien ne « disparaisse »
  // et que l'historique reste cohérent (2026-10-14).
  useEffect(() => {
    if (typeReleve !== 'definitif' || scope !== 'individuel'
        || !matriculeRecherche.trim() || !anneeAcademique) {
      setQrRepris(null);
      return;
    }
    let active = true;
    fetchQrExistant({ matricule: matriculeRecherche.trim(), semestre, annee_academique: anneeAcademique })
      .unwrap()
      .then((r) => {
        if (!active || !r?.existe) { if (active) setQrRepris(null); return; }
        if (r.qr_code_data) setQrCodeDataUrl(r.qr_code_data);
        if (r.numero_reference_externe) setNumeroReference(r.numero_reference_externe);
        const d = r.date_impression ? new Date(r.date_impression).toLocaleDateString('fr-FR') : '';
        setQrRepris(`QR code et n° de référence repris du relevé définitif déjà émis${d ? ` le ${d}` : ''}.`);
      })
      .catch(() => { if (active) setQrRepris(null); });
    return () => { active = false; };
  }, [typeReleve, scope, matriculeRecherche, semestre, anneeAcademique, fetchQrExistant]);
  const { data: historique } = useGetHistoriqueImpressionsQuery({
    annee_academique: histAnnee || undefined,
    type_releve: histType || undefined,
    date_debut: histDateDebut || undefined,
    date_fin: histDateFin || undefined,
  });

  // ── Import QR ──
  const handleQrImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setQrCodeDataUrl(ev.target?.result as string ?? null);
    reader.readAsDataURL(file);
  };

  // ── Validation pré-impression ──
  const validateParams = useCallback((): string => {
    if (typeReleve === 'definitif' && !qrCodeDataUrl) {
      return 'Veuillez importer le QR code pour un relevé définitif.';
    }
    if (scope === 'individuel' && !matriculeRecherche.trim()) {
      return 'Veuillez saisir un matricule.';
    }
    if ((scope === 'classe' || scope === 'filtre') && !classeCode) {
      return 'Veuillez sélectionner une classe.';
    }
    return '';
  }, [typeReleve, qrCodeDataUrl, scope, matriculeRecherche, classeCode]);

  // ── Charger les données ──
  const [alerteDette, setAlerteDette] = useState<string>('');
  // Accès léger scolarité (2026-09-12) : décision + matières à rattraper d'un redevable, sans notes
  const [releveLeger, setReleveLeger] = useState<ReleveLegerAgent | null>(null);
  const [pdfRattrapageEnCours, setPdfRattrapageEnCours] = useState(false);
  const { data: infoInstitut } = useGetInfoInstitutQuery();
  const loadData = useCallback(async (): Promise<DonneesReleve[]> => {
    setAlerteDette('');
    setReleveLeger(null);
    if (scope === 'individuel') {
      const result = await fetchDonneesReleve({
        matricule: matriculeRecherche.trim(),
        semestre,
        annee_academique: anneeAcademique,
      });
      // Fix 2026-09-07 : 403 dette → message métier explicite (pas générique)
      if ((result as any).error) {
        const err = (result as any).error;
        const data = err?.data;
        if (data && (data.code === 'dette_paiement' || data.mode === 'bloque_dette' || data.mode === 'bloque_absent')) {
          if (data.releve_leger) setReleveLeger(data.releve_leger as ReleveLegerAgent);
          throw new Error(data.error || 'Impression refusée : étudiant en situation de dette.');
        }
      }
      if (result.data) return [result.data];
      throw new Error('Étudiant introuvable ou données indisponibles.');
    } else {
      const result = await fetchDonneesClasse({
        classe_code: classeCode,
        semestre,
        annee_academique: anneeAcademique,
        moy_min: moyMin || undefined,
        moy_max: moyMax || undefined,
      });
      if (result.data) {
        // Fix 2026-09-07 : afficher le nb d'étudiants exclus pour dettes
        const exclus = result.data.etudiants_exclus_dette ?? [];
        if (exclus.length > 0) {
          const noms = exclus.slice(0, 5).map(e => `${e.nom} ${e.prenoms}`.trim()).join(', ');
          const suite = exclus.length > 5 ? ` (+${exclus.length - 5} autres)` : '';
          setAlerteDette(
            `${exclus.length} étudiant(s) exclu(s) pour dette de paiement : ${noms}${suite}. ` +
            `Seul un administrateur peut délivrer leur relevé.`
          );
        }
        return result.data.etudiants;
      }
      throw new Error('Données de classe indisponibles.');
    }
  }, [scope, matriculeRecherche, semestre, anneeAcademique, classeCode, moyMin, moyMax,
      fetchDonneesReleve, fetchDonneesClasse]);

  // ── Prévisualiser ──
  const handlePrevisualiser = async () => {
    const err = validateParams();
    if (err) { setErreur(err); return; }
    setErreur('');
    setLoading(true);
    try {
      const liste = await loadData();
      if (liste.length === 0) {
        setErreur('Aucun étudiant trouvé pour ce filtre.');
        return;
      }
      setPreviewList(liste);
      setPreviewIndex(0);
      setShowPreview(true);
    } catch (e: any) {
      setErreur(e.message ?? 'Erreur lors du chargement des données.');
    } finally {
      setLoading(false);
    }
  };

  // ── Imprimer tout ──
  const handleImprimerTout = async () => {
    const err = validateParams();
    if (err) { setErreur(err); return; }
    setErreur('');
    setLoading(true);
    try {
      const liste = await loadData();
      if (liste.length === 0) {
        setErreur('Aucun étudiant trouvé.');
        return;
      }

      // Fix 2026-09-14 : une feuille A4 par relevé. Une feuille trop haute (beaucoup de
      // matières) est réduite pour tenir sur sa page au lieu de déborder sur la suivante.
      const allHTML = liste.map((d) =>
        `<div class="feuille-a4">${buildReleveHTML(d, typeReleve, qrCodeDataUrl, numeroReference)
          .replace(/<!DOCTYPE html>[\s\S]*?<body[^>]*>/, '')
          .replace(/<\/body>[\s\S]*?<\/html>/, '')}</div>`
      ).join('');

      const fullHTML = documentA4({
        titre: `Releves_S${semestre}_${scope === 'individuel' ? matriculeRecherche : classeCode}`,
        css: 'body{font-family:Arial,Helvetica,sans-serif;color:#000;font-size:11px}'
          + '.feuille-a4{padding:0.8cm 1.2cm}.feuille-a4 tr{break-inside:avoid;page-break-inside:avoid}',
        corps: allHTML,
      });

      const w = window.open('', '_blank');
      if (!w) { setErreur("Impossible d'ouvrir la fenêtre d'impression. Vérifiez vos paramètres de popup."); return; }
      w.document.write(fullHTML);
      w.document.close();
      imprimerFenetreA4(w, '.feuille-a4').catch(() => { try { w.print(); } catch { /* fenêtre fermée */ } });

      // Enregistrer l'impression en arrière-plan
      await enregistrerImpression({
        matricule: scope === 'individuel' ? matriculeRecherche : undefined,
        classe_code: scope !== 'individuel' ? classeCode : undefined,
        type_releve: typeReleve,
        scope,
        semestre,
        annee_academique: anneeAcademique,
        nombre_copies: liste.length,
        numero_reference_externe: numeroReference || undefined,
        // Persiste le QR du relevé définitif pour la réimpression (2026-10-14).
        qr_code_data: typeReleve === 'definitif' ? (qrCodeDataUrl || undefined) : undefined,
      });
    } catch (e: any) {
      setErreur(e.message ?? "Erreur lors de l'impression.");
    } finally {
      setLoading(false);
    }
  };

  // ── Exporter Excel ──
  const handleExportExcel = async () => {
    if (!classeCode) { setErreur('Sélectionnez une classe pour exporter en Excel.'); return; }
    const state = store.getState() as any;
    const token = state.auth?.token ?? state.auth?.accessToken ?? '';
    const params = new URLSearchParams({ classe_code: classeCode, semestre: String(semestre), annee_academique: anneeAcademique });
    try {
      const resp = await fetch(`/api/releves/export-excel/?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error('Erreur serveur');
      const blob = await resp.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `releves_${classeCode}_S${semestre}_${anneeAcademique}.xlsx`;
      link.click();
    } catch {
      setErreur("Erreur lors de l'export Excel.");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">

      {/* ── Titre ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Relevés de Notes</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Impression des relevés provisoires et définitifs — Suivi des émissions
          </p>
        </div>
      </div>

      {/* ── Onglets ── */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-6">
          {([
            { id: 'impression', label: 'Impression', icon: <Printer size={16} /> },
            { id: 'historique', label: 'Historique', icon: <Clock size={16} /> },
          ] as const).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ═══════════════ ONGLET IMPRESSION ═══════════════ */}
      {activeTab === 'impression' && (
        <div className="space-y-5">

          {/* Erreur */}
          {erreur && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm flex items-center justify-between">
              {erreur}
              <button onClick={() => setErreur('')}><X size={14} /></button>
            </div>
          )}
          {/* Fix 2026-09-07 : bandeau étudiants exclus pour dettes */}
          {alerteDette && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-3 text-amber-800 dark:text-amber-200 text-sm flex items-start justify-between gap-2">
              <div className="flex-1">{alerteDette}</div>
              <button onClick={() => setAlerteDette('')} className="mt-0.5"><X size={14} /></button>
            </div>
          )}

          {/* Accès léger scolarité (2026-09-12) : décision d'un redevable, sans notes */}
          {releveLeger && (() => {
            const ues = releveLeger.ues_a_rattraper || [];
            const nbMatieres = ues.reduce((n, ue) => n + ue.matieres.length, 0);
            const e = releveLeger.etudiant;
            const imprimer = async () => {
              setPdfRattrapageEnCours(true);
              try {
                await genererPdfMatieresARattraper({
                  leger: releveLeger,
                  institut: {
                    ville: infoInstitut?.ville,
                    signataireTitre: infoInstitut?.chef_scolarite_titre,
                    signataireNom: infoInstitut?.chef_scolarite,
                  },
                });
              } catch (err: any) {
                setErreur(err?.message || 'Erreur lors de la génération du PDF.');
              } finally {
                setPdfRattrapageEnCours(false);
              }
            };
            return (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-sky-300 dark:border-sky-800 p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Accès léger — décision sans les notes
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {e.nom} {e.prenoms} · {e.matricule} · {e.classe} · Semestre {releveLeger.semestre} · {releveLeger.annee_academique}
                    </p>
                  </div>
                  <button onClick={() => setReleveLeger(null)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                </div>

                {!releveLeger.decision_disponible ? (
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {releveLeger.message || 'Les résultats de ce semestre ne sont pas encore disponibles.'}
                  </p>
                ) : releveLeger.valide ? (
                  <div className="flex items-center gap-2 text-sm font-semibold text-green-700 dark:text-green-400">
                    <CheckCircle size={16} /> Semestre validé — aucune matière à rattraper
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-sm font-semibold text-orange-700 dark:text-orange-400">
                      Semestre non validé — {nbMatieres} matière{nbMatieres > 1 ? 's' : ''} à rattraper
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {ues.map((ue) => (
                        <div key={ue.code_ue || ue.nom_ue} className="border border-gray-200 dark:border-gray-700 rounded p-3">
                          <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                            {ue.nom_ue}
                            {ue.prioritaire && (
                              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                prioritaire
                              </span>
                            )}
                          </div>
                          {ue.matieres.length > 0 ? (
                            <ul className="mt-1.5 space-y-0.5 text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                              {ue.matieres.map((m) => <li key={m.code_matiere || m.nom_matiere}>{m.nom_matiere}</li>)}
                            </ul>
                          ) : (
                            <div className="text-xs text-gray-500 mt-1">Aucune matière identifiée individuellement.</div>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={imprimer}
                      disabled={pdfRattrapageEnCours}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gray-900 hover:bg-gray-700 text-white dark:bg-gray-100 dark:hover:bg-gray-300 dark:text-gray-900 disabled:opacity-50"
                    >
                      <Printer size={15} />
                      {pdfRattrapageEnCours ? 'Génération…' : 'Imprimer la liste des matières à rattraper'}
                    </button>
                  </div>
                )}

                {releveLeger.periodes_dues.length > 0 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-2">
                    Relevé complet après régularisation de :{' '}
                    {releveLeger.periodes_dues.map((d) => (
                      d.present && d.solde && parseFloat(d.solde) > 0
                        ? `${d.label} (reste ${new Intl.NumberFormat('fr-FR').format(parseFloat(d.solde))} FCFA)`
                        : `${d.label} (non enregistré)`
                    )).join(' · ')}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Sélecteur de type ── */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Type de relevé</h3>
            <div className="flex gap-3">
              {([
                { val: 'provisoire', label: 'Relevé Provisoire', desc: 'Avec filigrane PROVISOIRE' },
                { val: 'definitif', label: 'Relevé Définitif', desc: 'Avec QR code d\'authentification' },
              ] as const).map(opt => (
                <button
                  key={opt.val}
                  onClick={() => setTypeReleve(opt.val)}
                  className={`flex-1 p-3 rounded-lg border-2 text-left transition-colors ${
                    typeReleve === opt.val
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className={`font-semibold text-sm ${typeReleve === opt.val ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}>
                    {opt.label}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* ── Paramètres ── */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Paramètres</h3>

            <div className="grid grid-cols-3 gap-4">
              {/* Année académique */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Année académique</label>
                <select
                  value={anneeAcademique}
                  onChange={e => { setAnneeAcademique(e.target.value); setAnneeUserModified(true); }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {annees.length === 0 && <option value="">— Chargement…</option>}
                  {annees.map(a => (
                    <option key={a.id} value={a.libelle}>{a.libelle}{a.est_active ? ' (active)' : ''}</option>
                  ))}
                </select>
              </div>

              {/* Pôle */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Pôle</label>
                <select
                  value={poleFilter}
                  onChange={e => { setPoleFilter(e.target.value); setClasseCode(''); }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Tous les pôles</option>
                  {['Polytechnique', 'Commerce', 'Juridique'].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              {/* Semestre */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Semestre</label>
                <div className="flex gap-3 mt-1">
                  {([1, 2] as const).map(s => (
                    <label key={s} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={semestre === s} onChange={() => setSemestre(s)} className="text-blue-600" />
                      <span className="text-sm text-gray-700 dark:text-gray-200">Semestre {s}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Portée */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Portée</label>
              <div className="flex gap-3">
                {([
                  { val: 'individuel', label: 'Étudiant individuel' },
                  { val: 'classe', label: 'Classe entière' },
                  { val: 'filtre', label: 'Filtre par moyenne' },
                ] as const).map(opt => (
                  <label key={opt.val} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={scope === opt.val} onChange={() => setScope(opt.val)} className="text-blue-600" />
                    <span className="text-sm text-gray-700 dark:text-gray-200">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Champs selon la portée */}
            {scope === 'individuel' && (
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Matricule étudiant</label>
                  <input
                    type="text"
                    value={matriculeRecherche}
                    onChange={e => setMatriculeRecherche(e.target.value.toUpperCase())}
                    placeholder="Ex: 2024L1001"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>
            )}

            {(scope === 'classe' || scope === 'filtre') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Classe</label>
                  <select
                    value={classeCode}
                    onChange={e => setClasseCode(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">— Sélectionner —</option>
                    {classes
                      .filter(c => !poleFilter || c.pole_nom === poleFilter)
                      .map(c => (
                      <option key={c.id} value={c.code}>{c.code} — {c.option_nom}</option>
                    ))}
                  </select>
                </div>
                {scope === 'filtre' && (
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Moy. min</label>
                      <input type="number" min="0" max="20" step="0.5" value={moyMin} onChange={e => setMoyMin(e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="0" />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Moy. max</label>
                      <input type="number" min="0" max="20" step="0.5" value={moyMax} onChange={e => setMoyMax(e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="20" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Section QR code (Définitif uniquement) ── */}
          {typeReleve === 'definitif' && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700 p-5">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-3 flex items-center gap-2">
                <Upload size={15} /> QR Code d'authentification (plateforme Laravel)
              </h3>
              <div className="flex items-start gap-6">
                {/* Import QR */}
                <div className="flex-1 space-y-2">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Image QR code (.png, .jpg)</label>
                  <input
                    ref={qrInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleQrImport}
                    className="text-sm text-gray-600 dark:text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer file:text-xs"
                  />
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mt-2">Numéro de référence</label>
                  <input
                    type="text"
                    value={numeroReference}
                    onChange={e => setNumeroReference(e.target.value)}
                    placeholder="Ex: 2I-2026-REL-E0FD71D06260DF3F"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  {qrRepris && (
                    <div className="flex items-start gap-1.5 text-[11px] text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded px-2 py-1.5 mt-1">
                      <CheckCircle size={13} className="shrink-0 mt-px" /> {qrRepris}
                    </div>
                  )}
                </div>
                {/* Aperçu QR */}
                {qrCodeDataUrl ? (
                  <div className="flex flex-col items-center gap-1">
                    <img src={qrCodeDataUrl} alt="QR Code" className="w-20 h-20 border border-gray-300 dark:border-gray-600 rounded" />
                    <button
                      onClick={() => { setQrCodeDataUrl(null); if (qrInputRef.current) qrInputRef.current.value = ''; }}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Supprimer
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 border-2 border-dashed border-amber-300 rounded flex items-center justify-center">
                    <span className="text-xs text-amber-400 text-center">QR<br/>ici</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Actions ── */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handlePrevisualiser}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              <Eye size={16} /> {loading ? 'Chargement…' : 'Prévisualiser'}
            </button>
            <button
              onClick={handleImprimerTout}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
            >
              <Printer size={16} /> Imprimer tout
            </button>
            {(scope === 'classe' || scope === 'filtre') && classeCode && (
              <button
                onClick={handleExportExcel}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
              >
                <FileDown size={16} /> Exporter Excel
              </button>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ ONGLET HISTORIQUE ═══════════════ */}
      {activeTab === 'historique' && (
        <div className="space-y-5">

          {/* Stats cards */}
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: 'Total imprimés', value: historique?.total ?? 0, color: 'blue', icon: <FileText size={20} /> },
              { label: 'Ce mois', value: historique?.ce_mois ?? 0, color: 'indigo', icon: <Clock size={20} /> },
              { label: 'Provisoires', value: historique?.provisoires ?? 0, color: 'amber', icon: <FileText size={20} /> },
              { label: 'Définitifs', value: historique?.definitifs ?? 0, color: 'green', icon: <CheckCircle size={20} /> },
            ].map(card => (
              <div key={card.label} className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{card.label}</span>
                  <span className={`text-${card.color}-500`}>{card.icon}</span>
                </div>
                <div className={`text-2xl font-bold text-${card.color}-600 dark:text-${card.color}-400`}>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          {/* Filtres */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex gap-3 flex-wrap">
              <input type="text" value={histAnnee} onChange={e => setHistAnnee(e.target.value)}
                placeholder="Année (ex: 2024-2025)"
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-40" />
              <select value={histType} onChange={e => setHistType(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">Tous les types</option>
                <option value="provisoire">Provisoire</option>
                <option value="definitif">Définitif</option>
              </select>
              <input type="date" value={histDateDebut} onChange={e => setHistDateDebut(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              <input type="date" value={histDateFin} onChange={e => setHistDateFin(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Date', 'Type', 'Portée', 'Étudiant / Classe', 'Semestre', 'N° Référence', 'Nb copies', 'Imprimé par'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {(historique?.impressions ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                      Aucune impression enregistrée
                    </td>
                  </tr>
                ) : (historique?.impressions ?? []).map(imp => (
                  <tr key={imp.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {new Date(imp.date_impression).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        imp.type_releve === 'provisoire'
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      }`}>
                        {imp.type_releve_display}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{imp.scope_display}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                      {imp.etudiant_nom ?? imp.classe_code ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">S{imp.semestre}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500 dark:text-gray-400 max-w-32 truncate" title={imp.numero_reference_externe}>
                      {imp.numero_reference_externe || '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">{imp.nombre_copies}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{imp.imprime_par_nom ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL PRÉVISUALISATION ═══════════════ */}
      {showPreview && previewList.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto">
          <div className="my-4 w-full max-w-4xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            {/* Header modal */}
            <div className="no-print flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-t-xl">
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Prévisualisation — {previewIndex + 1} / {previewList.length}
                <span className="ml-2 text-gray-400 dark:text-gray-500 font-normal">
                  {previewList[previewIndex]?.etudiant.matricule} — {previewList[previewIndex]?.etudiant.nom}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {previewList.length > 1 && (
                  <>
                    <button
                      onClick={() => setPreviewIndex(Math.max(0, previewIndex - 1))}
                      disabled={previewIndex === 0}
                      className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      onClick={() => setPreviewIndex(Math.min(previewList.length - 1, previewIndex + 1))}
                      disabled={previewIndex === previewList.length - 1}
                      className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Contenu */}
            <div className="overflow-y-auto max-h-[85vh]">
              <RelevePreview
                data={previewList[previewIndex]}
                typeReleve={typeReleve}
                qrCodeDataUrl={qrCodeDataUrl}
                numeroReference={numeroReference}
                onClose={() => setShowPreview(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelevesNotes;
